import type { PasswordPolicy } from '@qualicloud/contracts';

/**
 * Medidor de força de senha, ancorado na POLÍTICA REAL da organização.
 *
 * Não é um medidor genérico: cada requisito exibido vem de `passwordPolicy`,
 * que é o mesmo objeto que o IDENTITY aplica no servidor (§11.300(b)). Mostrar
 * "senha forte" para algo que o servidor vai recusar é pior do que não mostrar
 * nada — o usuário perde a confiança na tela e o suporte recebe chamado.
 *
 * A avaliação aqui é ORIENTAÇÃO. A decisão é do servidor, que ainda verifica
 * lista de senhas comuns e histórico de reuso — dois critérios que o cliente
 * não tem como avaliar sem vazar informação.
 */

export interface PasswordRequirement {
  readonly id: string;
  readonly label: string;
  readonly satisfied: boolean;
}

export type PasswordStrengthLevel = 'insufficient' | 'weak' | 'fair' | 'strong';

export interface PasswordAssessment {
  readonly requirements: readonly PasswordRequirement[];
  /** `true` quando TODOS os requisitos da política foram atendidos. */
  readonly meetsPolicy: boolean;
  readonly level: PasswordStrengthLevel;
  /** 0–100, para a barra de progresso. */
  readonly score: number;
  readonly levelLabel: string;
  /** Frase exibida abaixo da barra, com a próxima melhoria sugerida. */
  readonly advice: string;
}

const LEVEL_LABEL: Record<PasswordStrengthLevel, string> = {
  insufficient: 'Não atende à política',
  weak: 'Fraca',
  fair: 'Razoável',
  strong: 'Forte',
};

export function assessPassword(
  password: string,
  policy: PasswordPolicy,
): PasswordAssessment {
  const requirements: PasswordRequirement[] = [
    {
      id: 'minLength',
      label: `Pelo menos ${policy.minLength} caracteres`,
      satisfied: password.length >= policy.minLength,
    },
  ];

  if (policy.requireUppercase) {
    requirements.push({
      id: 'uppercase',
      label: 'Uma letra maiúscula',
      satisfied: /[A-ZÀ-Þ]/.test(password),
    });
  }
  if (policy.requireLowercase) {
    requirements.push({
      id: 'lowercase',
      label: 'Uma letra minúscula',
      satisfied: /[a-zà-þ]/.test(password),
    });
  }
  if (policy.requireDigit) {
    requirements.push({
      id: 'digit',
      label: 'Um número',
      satisfied: /\d/.test(password),
    });
  }
  if (policy.requireSymbol) {
    requirements.push({
      id: 'symbol',
      label: 'Um símbolo (! @ # $ % & * …)',
      satisfied: /[^\p{L}\p{N}]/u.test(password),
    });
  }

  const meetsPolicy = requirements.every((requirement) => requirement.satisfied);

  return {
    requirements,
    meetsPolicy,
    ...gradeStrength(password, policy, meetsPolicy),
  };
}

function gradeStrength(
  password: string,
  policy: PasswordPolicy,
  meetsPolicy: boolean,
): Pick<PasswordAssessment, 'level' | 'score' | 'levelLabel' | 'advice'> {
  if (!meetsPolicy) {
    // Enquanto a política não é atendida, a barra não passa de 25%: qualquer
    // valor maior sugeriria que a senha já serve.
    const satisfiedShare = password.length === 0 ? 0 : 0.25;
    return {
      level: 'insufficient',
      score: Math.round(satisfiedShare * 100),
      levelLabel: LEVEL_LABEL.insufficient,
      advice: 'Atenda a todos os requisitos abaixo para continuar.',
    };
  }

  // Acima do mínimo, o que mais agrega é COMPRIMENTO e variedade de classes.
  const classes = [
    /[a-zà-þ]/.test(password),
    /[A-ZÀ-Þ]/.test(password),
    /\d/.test(password),
    /[^\p{L}\p{N}]/u.test(password),
  ].filter(Boolean).length;

  const lengthBonus = Math.min(1, (password.length - policy.minLength) / 8);
  const varietyBonus = (classes - 1) / 3;
  const repetitionPenalty = hasObviousRepetition(password) ? 0.25 : 0;
  const sequencePenalty = hasSequentialRun(password) ? 0.2 : 0;

  const raw = 0.5 + lengthBonus * 0.3 + varietyBonus * 0.2 - repetitionPenalty - sequencePenalty;
  const score = Math.round(Math.max(0.3, Math.min(1, raw)) * 100);

  if (score >= 85) {
    return {
      level: 'strong',
      score,
      levelLabel: LEVEL_LABEL.strong,
      advice: 'Boa senha. Guarde-a em um gerenciador de senhas.',
    };
  }
  if (score >= 60) {
    return {
      level: 'fair',
      score,
      levelLabel: LEVEL_LABEL.fair,
      advice: 'Atende à política. Alongar a senha aumenta bastante a resistência.',
    };
  }
  return {
    level: 'weak',
    score,
    levelLabel: LEVEL_LABEL.weak,
    advice:
      'Atende à política, mas contém repetições ou sequências previsíveis. Prefira uma frase longa e sem padrão.',
  };
}

/** `aaa`, `111` — três ou mais caracteres iguais em sequência. */
function hasObviousRepetition(password: string): boolean {
  return /(.)\1{2,}/.test(password);
}

/** `abcd`, `1234`, `qwer` — quatro caracteres em progressão. */
function hasSequentialRun(password: string): boolean {
  const lower = password.toLowerCase();
  const keyboardRows = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm', '0123456789'];

  for (let index = 0; index + 3 < lower.length; index += 1) {
    const slice = lower.slice(index, index + 4);
    if (keyboardRows.some((row) => row.includes(slice) || [...row].reverse().join('').includes(slice))) {
      return true;
    }

    const codes = [...slice].map((char) => char.charCodeAt(0));
    const ascending = codes.every((code, i) => i === 0 || code === (codes[i - 1] as number) + 1);
    const descending = codes.every((code, i) => i === 0 || code === (codes[i - 1] as number) - 1);
    if (ascending || descending) return true;
  }

  return false;
}
